import { Avatar } from "@/components/ui/avatar";
import { User, Bot } from "lucide-react";
import ReactMarkdown from 'react-markdown';

interface ChatMessageProps {
  content: string;
  role?: "user" | "coordinator" | "advisor" | "interviewer";
  id?: string;
}

export function ChatMessage({ content, role = "user" }: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start w-full mb-2`}>
      <div className={`flex-shrink-0 ${isUser ? 'ml-2' : 'mr-2'}`}>
        <Avatar className={`h-6 w-6 md:h-8 md:w-8 flex items-center justify-center ${isUser ? 'bg-valeCreamYellow' : 'bg-valeYellow'}`}>
          {isUser ? (
            <User className="h-4 w-4 md:h-5 md:w-5 text-black" />
          ) : (
            <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
          )}
        </Avatar>
      </div>
      <div className={`flex flex-col ${isUser ? 'items-end ml-auto' : 'items-start'} max-w-[85%] md:max-w-[78%] min-w-0`}>
        <div
          className={`rounded-2xl px-3 py-1.5 break-words ${
            isUser
              ? 'bg-valeCreamYellow text-black'
              : 'bg-gray-100 text-gray-900'
          }`}
        >
          <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-full overflow-x-auto">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="last:mb-0 text-sm md:text-base">{children}</p>,
                ul: ({ children }) => <ul className="list-disc ml-4 mb-2 text-sm md:text-base">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal ml-4 mb-2 text-sm md:text-base">{children}</ol>,
                li: ({ children }) => <li className="mb-1">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                h1: ({ children }) => <h1 className="text-base md:text-lg font-bold mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm md:text-base font-bold mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-xs md:text-sm font-bold mb-2">{children}</h3>,
                a: ({ href, children }) => (
                  <a href={href} className="text-blue-500 hover:underline text-sm md:text-base" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                code: ({ children }) => (
                  <code className="bg-gray-200 dark:bg-gray-800 rounded px-1 py-0.5 text-sm md:text-base">{children}</code>
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}